<script lang="ts" setup>
import SiteLogo from '@/components/SiteLogo.vue';
import { computed, provide, ref, watch } from 'vue';
import SuspendedErrorHandler from '@/components/suspense/SuspendedErrorHandler.vue';
import AccountsDropdown from '@/components/general/AccountsDropdown.vue';
import { useDisplay } from 'vuetify';
import { useI18n } from 'vue-i18n';
import MailboxSidebar from '@/components/sidebar/MailboxSidebar.vue';
import AboutDialog from '@/components/AboutDialog.vue';
import QuickSettings from '@/components/QuickSettings.vue';
import SettingsSidebar from '@/components/sidebar/SettingsSidebar.vue';
import useIsSettingsView from '@/composables/useIsSettingsView';
import ComposeButton from '@/components/general/ComposeButton.vue';
import StorageUsage from '@/components/sidebar/StorageUsage.vue';
import { ROUTE_NAMES } from '@/router/conf';
import {
  onBeforeRouteUpdate,
  type RouteLocationAsRelativeGeneric,
  type RouteLocationNormalizedGeneric,
  useRoute,
  useRouter
} from 'vue-router';
import { REFRESH_SIDEBAR_MENU_ON_MOBILE } from '@/types/components/settings/folders/Consts';
import type { ISidebarRefreshCallback } from '@/types/components/settings/folders/Settings';
import useFoldersService from '@/composables/apiServices/useFoldersService';
import useSnackbar from '@/composables/useSnackbar';
import { useAuthChannel } from '@/composables/useAuthChannel';
import packageJson from '@/../package.json';
import SearchContainer from '@/components/general/SearchContainer.vue';
import useUserService from '@/composables/apiServices/useUserService';
import { useFavicon } from '@vueuse/core';
import { SIDEBAR_MENU_OPEN_INJECT_KEY } from '@/types/components/layouts/BaseLayout.consts';
import OnBoardingModal from '@/components/onBoarding/onBoardingModal.vue';
import { isOnboardingActive, useOnboarding } from '@/composables/onBoarding/useOnboarding';
import { registerToggleQuickSettings } from '@/composables/onBoarding/useOnboarding';
import { EFolderSystemSpecialUse } from '@/types/services/folder.service.enums';
import useSentryConfig from '@/composables/useSentryConfig';
import useSettingsService from '@/composables/apiServices/useSettingsService';
import dayjs from 'dayjs';
import useAuthPasswordRedirect from '@/composables/useAuthPasswordRedirect';
import AuthTimeoutNotification from '../general/AuthTimeoutNotification.vue';
import { waitForIt } from '@/helpers/utils';
import FoldersUnreadToggle from '../sidebar/FoldersUnreadToggle.vue';
import useIsMobilePlatform from '@/composables/useIsMobilePlatform.ts';

const display = useDisplay();
const { t } = useI18n();
const router = useRouter();
await router.isReady();

const route = useRoute();

useSentryConfig();
useAuthPasswordRedirect();

const { injectIsMobilePlatform } = useIsMobilePlatform();
const { isMobilePlatform } = injectIsMobilePlatform();
const { initializeAuthMessageWatcher } = useAuthChannel();
initializeAuthMessageWatcher();

const showQuickSettings = ref(false);
const { isSettingsLayoutRoute, isMatchingSettingsRoute } = useIsSettingsView();

const { getFolderWithSpecialUse } = useFoldersService();
const { data: inboxData } = getFolderWithSpecialUse(EFolderSystemSpecialUse.INBOX);

const { attributes, updateAttributes, details } = useUserService();
const { data: attributesData } = attributes();
const { mutateAsync: updateAttributesMutateAsync } = updateAttributes();
const { data: detailsData } = details();

const { onBoardingSteps, isWhatsNewAvailable, startWhatsNew } = await useOnboarding();

registerToggleQuickSettings((open) => toggleQuickSettings(open));

const { resetUpgrade } = useSettingsService();
const { mutateAsync: resetUpgradeMutate } = resetUpgrade();

const isSmallDisplay = computed(() => isMobilePlatform.value || display.smAndDown.value);
const showSidebar = ref(isSettingsLayoutRoute.value || !isSmallDisplay.value);
provide(SIDEBAR_MENU_OPEN_INJECT_KEY, {
  setNewValue: (newValue) => {
    showSidebar.value = isSmallDisplay.value ? newValue : true;
  }
});

const favicon = computed(() => {
  const value = inboxData.value?.unseen ?? 0;
  const modifier =
    value >= 30
      ? '-30-plus'
      : value >= 20
        ? '-20-plus'
        : value >= 10
          ? '-10-plus'
          : value > 0
            ? `-${value}`
            : '';
  const baseFavicon = import.meta.env.VITE_WEBMAIL_FAVICON ?? 'favicon.png';
  const [name, extension] = baseFavicon.split('.');
  return `${name}${modifier}.${extension}`;
});
useFavicon(favicon);

const currentPageTitle = computed(() => t((route.meta.label as string) ?? ''));

watch(isSmallDisplay, (newValue) => {
  showSidebar.value = !isSmallDisplay.value;
  if (newValue) {
    showQuickSettings.value = false;
  }
});

const handleSidebarRefresh: ISidebarRefreshCallback = (refresh) => {
  if (!refresh) {
    showSidebar.value = !showSidebar.value;
  }
  showSidebar.value = true;
};

provide(REFRESH_SIDEBAR_MENU_ON_MOBILE, handleSidebarRefresh);

const handleGoBack = async () => {
  if (!isSmallDisplay.value) {
    // Early exit
    return;
  }
  const newRoute =
    route.name !== ROUTE_NAMES.SETTINGS_LAYOUT
      ? router.resolve({ name: ROUTE_NAMES.SETTINGS_LAYOUT })
      : router.resolve({ name: ROUTE_NAMES.BASE_LAYOUT });
  if (newRoute) {
    await router.push(newRoute);
    showSidebar.value = newRoute.name === ROUTE_NAMES.SETTINGS_LAYOUT;
  } else {
    await router.push({ name: ROUTE_NAMES.BASE_LAYOUT });
  }
};

const getRedirectRoute = async (
  to: RouteLocationNormalizedGeneric
): Promise<void | RouteLocationAsRelativeGeneric> => {
  // When going here directly always redirect to the inbox

  if (to.name === ROUTE_NAMES.BASE_LAYOUT) {
    await waitForIt(async () => !!inboxData.value);
    if (!inboxData.value) {
      console.debug('No inbox found, redirecting to login');
      const { addSnackbar } = useSnackbar();
      addSnackbar({ message: t('errors.app.noInboxFound'), type: 'error' });
      return { name: ROUTE_NAMES.AUTH_LOGIN_PASSWORD, query: { reason: `noInboxFound` } };
    }

    // Always pure inbox redirect
    return {
      name: ROUTE_NAMES.MAILBOX,
      params: { userIndex: to.params.userIndex, mailboxId: inboxData.value.id }
    };
  }
};

const handleCloseSettingsSidebar = () => {
  if (isSmallDisplay.value) {
    // On small display close the sidebar
    showSidebar.value = false;
    return;
  }
};

const localSidebarWidth = ref<number>(256);
let isDraggingSidebar = false;
let latestClientX = 0;
let rafId: number | null = null;

const handleDragStart = (e: DragEvent) => {
  const img = new Image();
  // Transparent image
  img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAUEBAAAACwAAAAAAQABAAACAkQBADs=';
  document.body.appendChild(img);
  e.dataTransfer?.setDragImage(img, 0, 0);
  isDraggingSidebar = true;
  document.addEventListener('dragover', handleSidebarDragOver);
};

const handleSidebarDragOver = (e: DragEvent) => {
  // Use dragover event for cross-browser compatibility
  if (!isDraggingSidebar) return;
  e.preventDefault();

  if (e.clientX === 0) return;
  latestClientX = e.clientX;
  if (rafId === null) {
    rafId = requestAnimationFrame(updateSidebarWidth);
  }
};

function updateSidebarWidth() {
  localSidebarWidth.value = Math.max(256, Math.min(512, latestClientX - 12));
  rafId = null;
}

const handleDragEnd = async () => {
  isDraggingSidebar = false;
  document.removeEventListener('dragover', handleSidebarDragOver);
  if (rafId !== null) {
    cancelAnimationFrame(rafId);
    rafId = null;
  }
  if (!localSidebarWidth.value) {
    // Should never get here
    return;
  }
  await updateAttributesMutateAsync({ panelSizes: { sidePanel: localSidebarWidth.value } });
};

const toggleQuickSettings = (status?: boolean) => {
  showQuickSettings.value = status ?? !showQuickSettings.value;
  if (display.lgAndUp.value) {
    showSidebar.value = true;
  } else if (
    detailsData.value?.view_mode === '3-panel' &&
    display.md.value &&
    !isMatchingSettingsRoute.value
  ) {
    // In the mailbox view with 3 panels hide sidebar from MD and down
    showSidebar.value = !showQuickSettings.value;
  } else {
    showSidebar.value = !display.smAndDown.value;
  }
  // else if (display.mdAndDown.value) {
  //   // In every other version hide it in XS and SM
  //   showSidebar.value = !showQuickSettings.value;
  // } else {
  //   showSidebar.value = true;
  // }
};

watch(
  () => attributesData.value?.panelSizes?.sidePanel,
  (newPanelSize) => {
    localSidebarWidth.value = Number(newPanelSize?.toString().replace('px', '') ?? 256);
  }
);

watch(
  [
    () => display.mdAndDown.value,
    () => display.smAndDown.value,
    () => detailsData.value?.view_mode
  ],
  () => {
    toggleQuickSettings(showQuickSettings.value);
  }
);

watch(
  () => display.xs.value,
  () => {
    if (isSettingsLayoutRoute.value) {
      setTimeout(() => {
        showSidebar.value = true;
      }, 1);
    }
  }
);

watch(
  () => detailsData.value?.meta.upgrade_request?.requestedAt,
  (packageRequestAt) => {
    // Early exit if no upgrade request is set
    if (!packageRequestAt) {
      return;
    }
    const requestedAt = dayjs(packageRequestAt);
    const now = dayjs();
    const diffInHours = now.diff(requestedAt, 'hour');
    // If the upgrade was requested more than 24 hours ago, reset it
    if (diffInHours >= 24) {
      resetUpgradeMutate();
      return;
    } else {
      // Set a timeout to reset the upgrade when 24 hours is reached
      setTimeout(
        () => {
          resetUpgradeMutate();
        },
        (24 - diffInHours) * 60 * 60 * 1000
      );
    }
  },
  { immediate: true }
);

onBeforeRouteUpdate(async (to, from) => {
  if (to.params.userIndex !== from.params.userIndex) {
    return;
  }
  const redirectRoute = await getRedirectRoute(to);
  if (redirectRoute) {
    await router.replace(redirectRoute);
  }
});

watch(
  () => route.name,
  async () => {
    const redirectRoute = await getRedirectRoute(route);
    if (redirectRoute) {
      await router.replace(redirectRoute);
    }
  },
  { immediate: true }
);
watch(route, async () => {
  if (display.smAndDown.value && !isSettingsLayoutRoute.value) {
    showSidebar.value = false;
  }
});
</script>

<template>
  <VAppBar
    :class="[
      {
        'unset-index':
          (showSidebar && !isMatchingSettingsRoute && isSmallDisplay) || isOnboardingActive,
        'appbar--mobile': isSmallDisplay
      }
    ]"
    class="appbar"
    height="46"
  >
    <VAppBarTitle
      v-show="!isSmallDisplay"
      :style="{ width: localSidebarWidth - 20 + 'px' }"
      class="appbar__title"
    >
      <SiteLogo class="d-flex appbar__logo" />
    </VAppBarTitle>

    <SearchContainer
      v-if="!isMatchingSettingsRoute || !isSmallDisplay"
      :is-small-display="isSmallDisplay"
      @toggle-sidebar="
        () => {
          showSidebar = !showSidebar;
        }
      "
    />
    <div v-else class="pa-0 settings-header text-center d-flex flex-fill">
      <text-btn color="on-surface-variant" icon="mdi-arrow-left" @click="handleGoBack"></text-btn>

      <VAppBarTitle class="pr-12 py-2 appbar__settings-item-title" color="on-surface">
        {{ currentPageTitle }}
      </VAppBarTitle>
    </div>

    <template v-if="!isSmallDisplay" #append>
      <div class="appbar__actions">
        <VTooltip v-if="isWhatsNewAvailable" :text="t('onBoarding.actions.whatsNew')">
          <template #activator="{ props: tooltipActivatorProp }">
            <FilledBtn
              :elevation="0"
              class="appbar__action-button"
              color="primary"
              data-onboarding="whats-new-btn"
              data-test-id="whats-new-btn"
              flat
              size="40"
              v-bind="tooltipActivatorProp"
              @click.stop="startWhatsNew"
            >
              <v-icon color="on-primary">mdi-star-outline</v-icon>
            </FilledBtn>
          </template>
        </VTooltip>
        <VTooltip :text="showQuickSettings ? t('quickSettings.close') : t('quickSettings.open')">
          <template #activator="{ props: tooltipActivatorProp }">
            <FilledBtn
              :elevation="0"
              class="appbar__action-button"
              color="primary"
              data-onboarding="quick-settings-btn"
              flat
              icon="mdi-cog-outline"
              size="40"
              v-bind="tooltipActivatorProp"
              @click.stop="() => toggleQuickSettings()"
            >
              <v-icon color="on-primary">mdi-cog-outline</v-icon>
            </FilledBtn>
          </template>
        </VTooltip>
        <AccountsDropdown />
      </div>
    </template>
  </VAppBar>

  <VNavigationDrawer
    v-model="showSidebar"
    data-onboarding="sidebar-drawer"
    :class="{
      'w-100': showSidebar && isMatchingSettingsRoute && isSmallDisplay,
      'mobile__sidebar-top': showSidebar && !isMatchingSettingsRoute && isSmallDisplay
    }"
    :temporary="isSmallDisplay && !isMatchingSettingsRoute"
    :width="
      showSidebar && isMatchingSettingsRoute && isSmallDisplay
        ? '100vw'
        : isSmallDisplay && !isMatchingSettingsRoute
          ? 320
          : localSidebarWidth
    "
    class="sidebar pt-md-3"
    floating
    location="left"
    disable-resize-watcher
    disable-route-watcher
    touchless
  >
    <v-row density="comfortable" class="pl-4" v-if="!isSmallDisplay">
      <FoldersUnreadToggle v-if="!isMatchingSettingsRoute" />
      <ComposeButton />
    </v-row>

    <MailboxSidebar v-if="!isMatchingSettingsRoute" :is-small-display="isSmallDisplay" />

    <SettingsSidebar
      v-else
      :is-small-display="isSmallDisplay"
      @close-sidebar="handleCloseSettingsSidebar"
    />

    <template #append>
      <div class="px-4 py-2">
        <StorageUsage />
        <!-- <p class="text-label-lg text-center" sentry-unmask>{{ t('sidebar.poweredBy') }}</p> -->
        <AboutDialog button-variant="text">
          <template #activator="activatorProps">
            <button
              v-bind="activatorProps"
              class="text-label-lg text-center d-flex justify-space-between w-100 sidebar__about-button"
              sentry-unmask
            >
              <span>{{ `Version: ${packageJson.version}` }}</span>
              <span>{{ t('about.link') }}</span>
            </button>
          </template>
        </AboutDialog>
      </div>
    </template>
  </VNavigationDrawer>

  <VMain class="d-flex layout__main">
    <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions-->
    <div
      v-if="!isMatchingSettingsRoute && !isSmallDisplay"
      class="size-drag-divider"
      draggable="true"
      @dragstart="handleDragStart"
      @dragend.prevent="handleDragEnd"
    >
      <v-divider draggable="true" opacity="1" vertical />
    </div>
    <SuspendedErrorHandler>
      <!-- Current view -->
      <div
        :class="{
          'px-4': isMatchingSettingsRoute,
          'pr-6': !isSmallDisplay,
          'pt-6': !isSmallDisplay,
          'layout__base--settings': isMatchingSettingsRoute
        }"
        class="d-flex flex-grow-1 flex-column layout__base w-100"
      >
        <RouterView />
      </div>
    </SuspendedErrorHandler>
  </VMain>

  <v-navigation-drawer
    v-model="showQuickSettings"
    data-onboarding="quick-settings-drawer"
    class="pr-3"
    width="311"
    floating
    location="right"
    disable-resize-watcher
    disable-route-watcher
    touchless
  >
    <QuickSettings @close="() => toggleQuickSettings()" />
  </v-navigation-drawer>
  <OnBoardingModal v-if="onBoardingSteps.length > 0" />
  <AuthTimeoutNotification />
</template>

<style lang="scss" scoped>
@use '../../assets/styles/settings' as settings;
@use 'sass:map';
@use '../../assets/styles/components/size-drag-divider';

.unset-index {
  z-index: unset !important;
}

.appbar {
  background: rgb(var(--v-theme-navbar-mobile));

  &--highlighted {
    z-index: 1009 !important;
  }

  &:not(&--mobile) {
    background: rgb(var(--v-theme-navbar));
  }

  &__title {
    flex: none;
    min-width: 14.5rem;
    width: 100%;
    display: flex;
    justify-content: center;
  }

  &__logo {
    padding-left: 0.75rem;
  }

  &__actions {
    display: flex;
    gap: 1rem;
    padding-right: 1.25rem;
  }

  &__settings-item-title {
    font-size: 1.5rem;
    line-height: 2rem;
  }

  &__action-button {
    border: none;
    background: rgb(var(--v-theme-initials-btn)) !important;
    color: rgb(var(--v-theme-initials-btn-text)) !important;

    &:hover {
      border: 1px solid rgb(var(--v-theme-initials-btn-hover));
    }

    &:active {
      border: 1px solid rgb(var(--v-theme-on-primary));
      color: rgb(var(--v-theme-on-primary));
    }

    &--highlighted {
      z-index: 1010 !important;
    }

    // &:hover {
    //   box-shadow: none !important;
    //   border: 1px solid rgb(var(--v-theme-on-primary));

    //   &:deep(> .v-btn__overlay) {
    //     opacity: 0 !important;
    //   }
    // }
  }

  .v-toolbar__content > .v-toolbar-title {
    margin-inline-start: 0;
  }
}

.sidebar {
  transition: none !important;
  display: flex;
  flex-direction: column;
  background-color: rgb(var(--v-theme-surface));

  &__about-button {
    background-color: transparent;
    border-style: none;

    &:hover {
      position: relative;

      &::before {
        content: '';
        pointer-events: none;
        position: absolute;
        inset: -0.125rem -0.25rem;
        border-radius: 0.375rem;
        background-color: rgb(var(--v-theme-primary), 0.08);
      }
    }
  }
}

.layout {
  &__base {
    background-color: rgb(var(--v-theme-surface));
    overflow: auto;

    &--settings {
      background-color: rgb(var(--v-theme-white));
    }
  }

  &__main {
    transition: none !important;

    &:has(> .v-alert) {
      align-items: center;
    }
  }
}

.mobile {
  &__sidebar {
    &-top {
      height: 100% !important;
      z-index: 1009 !important;
      top: 0 !important;
    }
  }
}
</style>
